import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Building2, MapPin, Globe, Phone, Mail, Users, Shield, X,
  LayoutGrid, List, Home, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgencyScrollRow } from '@/components/agencies/AgencyScrollRow';
import { TitaniumAgencyCard } from '@/components/agencies/TitaniumAgencyCard';
import { FeaturedAgencyCard } from '@/components/agencies/FeaturedAgencyCard';
import { CityAgencyCard } from '@/components/agencies/CityAgencyCard';
import { AgencyAdsSidebar } from '@/components/agencies/AgencyAdsSidebar';

const CITIES = ['Addis Ababa', 'Adama', 'Hawassa', 'Bahir Dar', 'Mekelle', 'Dire Dawa', 'Jimma', 'Gondar'];

export default function Agencies() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState<string | undefined>(searchParams.get('city') || undefined);
  const [sortBy, setSortBy] = useState('name_asc');
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');

  useEffect(() => {
    const c = searchParams.get('city');
    if (c) setCityFilter(c);
  }, [searchParams]);

  const { data: agencies, isLoading } = useQuery({
    queryKey: ['agencies-page'],
    queryFn: async () => {
      const { data, error } = await supabase.from('agencies').select('*').order('name');
      if (error) throw error;
      const agencyIds = (data || []).map(a => a.id);
      const { data: agentProfiles } = await supabase
        .from('agent_profiles')
        .select('user_id, agency_id')
        .in('agency_id', agencyIds);
      const agentCounts: Record<string, number> = {};
      const userToAgency: Record<string, string> = {};
      (agentProfiles || []).forEach((p: any) => {
        if (p.agency_id) agentCounts[p.agency_id] = (agentCounts[p.agency_id] || 0) + 1;
        if (p.user_id && p.agency_id) userToAgency[p.user_id] = p.agency_id;
      });

      const userIds = Object.keys(userToAgency);
      const counts: Record<string, { sale: number; rent: number }> = {};
      if (userIds.length) {
        const { data: props } = await supabase
          .from('properties')
          .select('user_id, listing_type')
          .in('user_id', userIds)
          .eq('status', 'active')
          .eq('approval_status', 'approved');
        (props || []).forEach((p: any) => {
          const aid = userToAgency[p.user_id];
          if (!aid) return;
          if (!counts[aid]) counts[aid] = { sale: 0, rent: 0 };
          if (p.listing_type === 'sale') counts[aid].sale += 1;
          else if (p.listing_type === 'rent') counts[aid].rent += 1;
        });
      }

      // Fetch active package tier per agency
      const tierMap: Record<string, string | null> = {};
      const { data: tiers } = await supabase
        .from('agency_featured_tier' as any)
        .select('agency_id, tier')
        .in('agency_id', agencyIds);
      (tiers || []).forEach((t: any) => { tierMap[t.agency_id] = t.tier; });

      return (data || []).map(a => ({
        ...a,
        agentCount: agentCounts[a.id] || 0,
        saleCount: counts[a.id]?.sale || 0,
        rentCount: counts[a.id]?.rent || 0,
        tier: tierMap[a.id] || null,
      }));
    },
  });

  const titaniumAgencies = useMemo(() => {
    const list = agencies || [];
    const tier = list.filter((a: any) => a.subscription_tier === 'titanium');
    if (tier.length >= 4) return tier.slice(0, 12);
    return [...list]
      .sort((a: any, b: any) => (b.saleCount + b.rentCount) - (a.saleCount + a.rentCount))
      .slice(0, 8);
  }, [agencies]);

  const featuredAgencies = useMemo(() => {
    const list = agencies || [];
    const titaniumIds = new Set(titaniumAgencies.map((a: any) => a.id));
    const feat = list.filter((a: any) =>
      !titaniumIds.has(a.id) && (a.is_featured || ['platinum', 'gold'].includes(a.subscription_tier))
    );
    if (feat.length >= 4) return feat.slice(0, 12);
    return list
      .filter((a: any) => !titaniumIds.has(a.id))
      .sort((a: any, b: any) => (b.saleCount + b.rentCount) - (a.saleCount + a.rentCount))
      .slice(0, 10);
  }, [agencies, titaniumAgencies]);

  const cityCounts = useMemo(() => {
    const map: Record<string, number> = {};
    (agencies || []).forEach((a: any) => { if (a.city) map[a.city] = (map[a.city] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 10);
  }, [agencies]);

  const filtered = (agencies || []).filter(a => {
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (cityFilter && a.city !== cityFilter) return false;
    return true;
  }).sort((a: any, b: any) => {
    if (sortBy === 'name_asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name_desc') return b.name.localeCompare(a.name);
    if (sortBy === 'agents') return b.agentCount - a.agentCount;
    if (sortBy === 'most_listings') return ((b.saleCount + b.rentCount) - (a.saleCount + a.rentCount));
    if (sortBy === 'most_sales') return (b.saleCount - a.saleCount);
    if (sortBy === 'most_rent') return (b.rentCount - a.rentCount);
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const activeFilterCount = [cityFilter].filter(Boolean).length;

  return (
    <Layout>
      <SEOHead
        title="Real Estate Agencies in Ethiopia — Verified Agents"
        description="Find verified real estate agencies and agents in Ethiopia. Connect with trusted professionals for buying, selling and renting property."
        canonical="/agencies"
      />
      <div className="border-b bg-muted/30">
        <div className="container py-4">
          <nav className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Link to="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRightIcon className="h-3 w-3" />
            <span className="text-foreground font-medium">Agencies</span>
          </nav>
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl md:text-2xl font-bold">Real Estate Agencies</h1>
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span> agenc{filtered.length !== 1 ? 'ies' : 'y'} found
            </p>
          </div>
        </div>
      </div>

      {/* Sticky Filter Bar */}
      <div className="sticky top-[56px] md:top-[64px] lg:top-[117px] z-30 bg-background border-b shadow-sm">
        <div className="container">
          <div className="flex items-center gap-2 py-3 overflow-x-auto scrollbar-hide">
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search agencies..." value={search} onChange={e => setSearch(e.target.value)} className="h-9 w-52 pl-8 text-xs" />
            </div>

            <Select value={cityFilter || 'all'} onValueChange={v => setCityFilter(v === 'all' ? undefined : v)}>
              <SelectTrigger className="h-9 w-auto min-w-[130px] text-xs shrink-0 gap-1.5">
                <Home className="h-3.5 w-3.5 text-muted-foreground" /><SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {CITIES.map(c => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="h-6 w-px bg-border shrink-0 mx-1" />

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-9 w-auto min-w-[150px] text-xs shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="most_listings">Most Listings</SelectItem>
                <SelectItem value="most_sales">Most for Sale</SelectItem>
                <SelectItem value="most_rent">Most for Rent</SelectItem>
                <SelectItem value="agents">Most Agents</SelectItem>
                <SelectItem value="name_asc">Name A-Z</SelectItem>
                <SelectItem value="name_desc">Name Z-A</SelectItem>
              </SelectContent>
            </Select>

            <div className="hidden md:flex border rounded-lg overflow-hidden shrink-0 ml-auto">
              <Button variant={layout === 'grid' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('grid')}><LayoutGrid className="h-4 w-4" /></Button>
              <Button variant={layout === 'list' ? 'secondary' : 'ghost'} size="icon" className="h-9 w-9 rounded-none" onClick={() => setLayout('list')}><List className="h-4 w-4" /></Button>
            </div>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 pb-3 overflow-x-auto scrollbar-hide">
              <span className="text-[10px] text-muted-foreground shrink-0 uppercase tracking-wider font-medium">Active:</span>
              {cityFilter && <Badge variant="secondary" className="text-[10px] gap-1 shrink-0">{cityFilter}<X className="h-2.5 w-2.5 cursor-pointer" onClick={() => setCityFilter(undefined)} /></Badge>}
              <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive shrink-0" onClick={() => setCityFilter(undefined)}>Clear All</Button>
            </div>
          )}
        </div>
      </div>

      {/* Featured Rows + Sponsored Sidebar */}
      <div className="container py-6 grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          {isLoading ? (
            <div className="space-y-6">
              <Skeleton className="h-40 rounded-2xl" />
              <Skeleton className="h-40 rounded-2xl" />
              <Skeleton className="h-32 rounded-2xl" />
            </div>
          ) : (agencies?.length || 0) > 0 ? (
            <>
              {titaniumAgencies.length > 0 && (
                <AgencyScrollRow title="Titanium Agencies">
                  {titaniumAgencies.map((a: any, i: number) => (
                    <TitaniumAgencyCard
                      key={a.id}
                      id={a.id}
                      name={a.name}
                      logoUrl={a.logo_url}
                      saleCount={a.saleCount}
                      rentCount={a.rentCount}
                      highlighted={i === 0}
                      tier={a.tier as any}
                    />
                  ))}
                </AgencyScrollRow>
              )}

              {featuredAgencies.length > 0 && (
                <AgencyScrollRow title="Featured Agencies">
                  {featuredAgencies.map((a: any) => (
                    <FeaturedAgencyCard
                      key={a.id}
                      id={a.id}
                      name={a.name}
                      logoUrl={a.logo_url}
                      city={a.city}
                      saleCount={a.saleCount}
                      rentCount={a.rentCount}
                      tier={a.tier as any}
                    />
                  ))}
                </AgencyScrollRow>
              )}

              {cityCounts.length > 0 && (
                <AgencyScrollRow title="Browse Agencies By City">
                  {cityCounts.map(([city, count]) => (
                    <CityAgencyCard key={city} city={city} count={count} />
                  ))}
                </AgencyScrollRow>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <Building2 className="h-14 w-14 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold mb-1">No agencies found</h3>
              <p className="text-muted-foreground text-sm">Try adjusting your search</p>
            </div>
          )}

          {/* Mobile / tablet sponsored row — shown below the agencies list */}
          <div className="lg:hidden mt-8">
            <AgencyAdsSidebar
              placement="agencies_inline_mobile"
              variant="horizontal"
              heading="Sponsored"
              limit={6}
            />
          </div>
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-[160px]">
            <AgencyAdsSidebar />
          </div>
        </aside>
      </div>
    </Layout>
  );
}
